import { useEffect, useState, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { backOrigin, useBackTo } from "@/hooks/useBackTo";
import { useForm, type Control, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { ApiError } from "@/lib/apiClient";
import { toastApiError } from "@/lib/toast";
import { applicationCampaigns as campaignsApi, CAMPAIGN_STATUSES, CAMPAIGN_TYPES, type CampaignDetail } from "@/features/applications/api/applications.api";
import { Notice } from "@/components/Notice";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { TextField, TextAreaField, SelectField, NumberField, CheckField, useAutoFocus, useUnsavedChangesWarning } from "@/components/form";
import { FIELD_MAX, tooLong } from "@/lib/fieldLimits";

const num = z.number().min(0, "Value cannot be negative.").nullable();

const schema = z.object({
  name: z.string().trim().min(1, "Campaign name is required.").max(FIELD_MAX.appCampaign.name, tooLong("Name", FIELD_MAX.appCampaign.name)),
  description: z.string().max(FIELD_MAX.appCampaign.description, tooLong("Description", FIELD_MAX.appCampaign.description)),
  isPublic: z.boolean(),
  campaignType: z.string(),
  acquirer: z.string().max(FIELD_MAX.appCampaign.acquirer, tooLong("Acquirer", FIELD_MAX.appCampaign.acquirer)),
  boardingApplicationType: z.string(),
  giactKbaEnabled: z.boolean(),
  status: z.string(),
  acquiringFeeSpecialNote: z.string().max(FIELD_MAX.appCampaign.acquiringFeeSpecialNote, tooLong("Acquiring fee special note", FIELD_MAX.appCampaign.acquiringFeeSpecialNote)),
  achFeeSpecialNote: z.string().max(FIELD_MAX.appCampaign.achFeeSpecialNote, tooLong("ACH fee special note", FIELD_MAX.appCampaign.achFeeSpecialNote)),
  tsysAssociation: z.string().max(FIELD_MAX.appCampaign.tsysAssociation, tooLong("TSYS association", FIELD_MAX.appCampaign.tsysAssociation)),
  leadSource: z.string().max(FIELD_MAX.appCampaign.leadSource, tooLong("Lead source", FIELD_MAX.appCampaign.leadSource)),

  visaMcDiscoverRate: num,
  americanExpressOptBlueRate: num,
  qualifiedRate: num,
  midQualSurcharge: num,
  nonQualSurcharge: num,
  passThroughInterchangePlus: num,
  pinDebitRate: num,
  ebtRate: num,
  offlineDebitRate: num,
  offlineDebitPassThrough: num,
  pinDebitRatePassThrough: num,

  transactionFee: num,
  authorizationFee: num,
  avsFee: num,
  voiceAuthorizationFee: num,
  pinDebitAuthorizationFee: num,
  ebtAuthorizationFee: num,
  batchFee: num,
  perTransFee: num,

  monthlyFee: num,
  monthlyDashboardAccessFee: num,
  monthlyPciFee: num,
  monthlyMinimumFee: num,
  annualFee: num,
  serviceProviderMonthlyFee: num,
  otherServiceProviderFee: num,

  setupFee: num,
  applicationFee: num,
  chargebackFee: num,
  retrievalFee: num,
  gatewayFee: num,
  wirelessFee: num,
  posSystemFee: num,
});

type FormValues = z.infer<typeof schema>;

function toForm(c: CampaignDetail): FormValues {
  return {
    name: c.name,
    description: c.description ?? "",
    isPublic: c.isPublic,
    campaignType: c.campaignType,
    acquirer: c.acquirer ?? "",
    boardingApplicationType: c.boardingApplicationType,
    giactKbaEnabled: c.giactKbaEnabled,
    status: c.status,
    acquiringFeeSpecialNote: c.acquiringFeeSpecialNote ?? "",
    achFeeSpecialNote: c.achFeeSpecialNote ?? "",
    tsysAssociation: c.tsysAssociation ?? "",
    leadSource: c.leadSource ?? "",
    visaMcDiscoverRate: c.visaMcDiscoverRate,
    americanExpressOptBlueRate: c.americanExpressOptBlueRate,
    qualifiedRate: c.qualifiedRate,
    midQualSurcharge: c.midQualSurcharge,
    nonQualSurcharge: c.nonQualSurcharge,
    passThroughInterchangePlus: c.passThroughInterchangePlus,
    pinDebitRate: c.pinDebitRate,
    ebtRate: c.ebtRate,
    offlineDebitRate: c.offlineDebitRate,
    offlineDebitPassThrough: c.offlineDebitPassThrough,
    pinDebitRatePassThrough: c.pinDebitRatePassThrough,
    transactionFee: c.transactionFee,
    authorizationFee: c.authorizationFee,
    avsFee: c.avsFee,
    voiceAuthorizationFee: c.voiceAuthorizationFee,
    pinDebitAuthorizationFee: c.pinDebitAuthorizationFee,
    ebtAuthorizationFee: c.ebtAuthorizationFee,
    batchFee: c.batchFee,
    perTransFee: c.perTransFee,
    monthlyFee: c.monthlyFee,
    monthlyDashboardAccessFee: c.monthlyDashboardAccessFee,
    monthlyPciFee: c.monthlyPciFee,
    monthlyMinimumFee: c.monthlyMinimumFee,
    annualFee: c.annualFee,
    serviceProviderMonthlyFee: c.serviceProviderMonthlyFee,
    otherServiceProviderFee: c.otherServiceProviderFee,
    setupFee: c.setupFee,
    applicationFee: c.applicationFee,
    chargebackFee: c.chargebackFee,
    retrievalFee: c.retrievalFee,
    gatewayFee: c.gatewayFee,
    wirelessFee: c.wirelessFee,
    posSystemFee: c.posSystemFee,
  };
}

function fromForm(v: FormValues): Partial<CampaignDetail> & { name: string } {
  const text = (s: string) => s.trim() || null;
  return {
    name: v.name.trim(),
    description: text(v.description),
    isPublic: v.isPublic,
    campaignType: v.campaignType,
    acquirer: text(v.acquirer),
    boardingApplicationType: v.boardingApplicationType,
    giactKbaEnabled: v.giactKbaEnabled,
    status: v.status,
    acquiringFeeSpecialNote: text(v.acquiringFeeSpecialNote),
    achFeeSpecialNote: text(v.achFeeSpecialNote),
    tsysAssociation: text(v.tsysAssociation),
    leadSource: text(v.leadSource),
    visaMcDiscoverRate: v.visaMcDiscoverRate,
    americanExpressOptBlueRate: v.americanExpressOptBlueRate,
    qualifiedRate: v.qualifiedRate,
    midQualSurcharge: v.midQualSurcharge,
    nonQualSurcharge: v.nonQualSurcharge,
    passThroughInterchangePlus: v.passThroughInterchangePlus,
    pinDebitRate: v.pinDebitRate,
    ebtRate: v.ebtRate,
    offlineDebitRate: v.offlineDebitRate,
    offlineDebitPassThrough: v.offlineDebitPassThrough,
    pinDebitRatePassThrough: v.pinDebitRatePassThrough,
    transactionFee: v.transactionFee,
    authorizationFee: v.authorizationFee,
    avsFee: v.avsFee,
    voiceAuthorizationFee: v.voiceAuthorizationFee,
    pinDebitAuthorizationFee: v.pinDebitAuthorizationFee,
    ebtAuthorizationFee: v.ebtAuthorizationFee,
    batchFee: v.batchFee,
    perTransFee: v.perTransFee,
    monthlyFee: v.monthlyFee,
    monthlyDashboardAccessFee: v.monthlyDashboardAccessFee,
    monthlyPciFee: v.monthlyPciFee,
    monthlyMinimumFee: v.monthlyMinimumFee,
    annualFee: v.annualFee,
    serviceProviderMonthlyFee: v.serviceProviderMonthlyFee,
    otherServiceProviderFee: v.otherServiceProviderFee,
    setupFee: v.setupFee,
    applicationFee: v.applicationFee,
    chargebackFee: v.chargebackFee,
    retrievalFee: v.retrievalFee,
    gatewayFee: v.gatewayFee,
    wirelessFee: v.wirelessFee,
    posSystemFee: v.posSystemFee,
  };
}

export default function CampaignEditPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const backTo = useBackTo("/manage/application-campaigns");
  const isEdit = id !== undefined;
  const confirm = useConfirm();

  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toForm(blankCampaign()),
  });

  useAutoFocus(form, "name", !isEdit);
  useUnsavedChangesWarning(form.formState.isDirty);

  useEffect(() => {
    let cancelled = false;
    if (isEdit && id) {
      campaignsApi.get(id)
        .then((c) => {
          if (cancelled) return;
          form.reset(toForm(c));
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          if (e instanceof ApiError && e.status === 404) setNotFound(true);
          else setError(e instanceof Error ? e.message : "Failed to load campaign.");
          setLoading(false);
        });
    }
    return () => { cancelled = true; };
  }, [id, isEdit, form]);

  async function onSubmit(values: FormValues) {
    if (isEdit && !form.formState.isDirty) {
      toast.info("No changes to save");
      return;
    }
    setError(null);
    const body = fromForm(values);
    try {
      if (isEdit && id) {
        await campaignsApi.update(id, body);
        const fresh = await campaignsApi.get(id);
        form.reset(toForm(fresh));
        toast.success("Campaign updated");
      } else {
        const created = await campaignsApi.create(body);
        toast.success("Campaign created");
        navigate(`/manage/application-campaigns/${created.id}`, { replace: true, state: backOrigin(backTo) });
      }
    } catch (e) {
      toastApiError(e, "Save failed.");
    }
  }

  function onInvalid() {
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus({ preventScroll: true });
    });
  }

  async function onDelete() {
    if (!id) return;
    if (!(await confirm({ description: "Delete this campaign? Applications already created from it are unaffected.", confirmLabel: "Delete", destructive: true }))) {
      return;
    }
    setError(null);
    try {
      await campaignsApi.remove(id);
      navigate(backTo, { replace: true });
    } catch (e) {
      toastApiError(e, "Delete failed.");
    }
  }

  return (
    <>
      <div className="mb-4">
        <Link
          to={backTo}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to campaigns
        </Link>
      </div>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">
            {isEdit ? "Edit campaign" : "New campaign"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Fee fields left blank are not copied into new applications —
            the agent fills them in case-by-case.
          </p>
        </div>
        {isEdit && (
          <Button type="button" variant="destructive" onClick={onDelete}>
            Delete campaign
          </Button>
        )}
      </header>

      {notFound ? (
        <Notice tone="info">Campaign not found.</Notice>
      ) : loading ? (
        <Loading>Loading campaign…</Loading>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6">
            <Panel title="Identity">
              <Grid>
                <TextField control={form.control} name="name" label="Name" required />
                <TextField control={form.control} name="acquirer" label="Acquirer" placeholder="e.g. FFB Bank" />
              </Grid>
              <div className="mt-4">
                <TextAreaField control={form.control} name="description" label="Description" rows={2} />
              </div>
            </Panel>

            <Panel title="Details">
              <Grid>
                <SelectField control={form.control} name="campaignType" label="Campaign type" options={CAMPAIGN_TYPES.map((o) => [o, o])} />
                <SelectField control={form.control} name="boardingApplicationType" label="Boarding application type" options={CAMPAIGN_TYPES.map((o) => [o, o])} />
                <SelectField control={form.control} name="status" label="Status" options={CAMPAIGN_STATUSES.map((o) => [o, o])} />
                <CheckField control={form.control} name="isPublic" label="Public — visible to all agents in the campaign picker" className="self-end mb-2" />
                <CheckField control={form.control} name="giactKbaEnabled" label="GIACT KBA enabled" className="self-end mb-2" />
              </Grid>
              <div className="mt-4">
                <Grid>
                  <TextField control={form.control} name="tsysAssociation" label="TSYS association" />
                  <TextField control={form.control} name="leadSource" label="Lead source" />
                </Grid>
              </div>
            </Panel>

            <Panel title="Special Notes">
              <TextAreaField control={form.control} name="acquiringFeeSpecialNote" label="Acquiring fee special note" rows={2} />
              <div className="mt-4">
                <TextAreaField control={form.control} name="achFeeSpecialNote" label="ACH fee special note" rows={2} />
              </div>
            </Panel>

            <Panel title="Discount Rates">
              <Grid cols={3}>
                <NumberField control={form.control} name="visaMcDiscoverRate" label="Visa / MC / Discover rate" suffix="%" min={0} />
                <NumberField control={form.control} name="americanExpressOptBlueRate" label="American Express OptBlue rate" suffix="%" min={0} />
                <NumberField control={form.control} name="qualifiedRate" label="Qualified rate" suffix="%" min={0} />
                <NumberField control={form.control} name="midQualSurcharge" label="Mid-qual surcharge" suffix="%" min={0} />
                <NumberField control={form.control} name="nonQualSurcharge" label="Non-qual surcharge" suffix="%" min={0} />
                <NumberField control={form.control} name="passThroughInterchangePlus" label="Pass-through interchange plus" suffix="%" min={0} />
                <NumberField control={form.control} name="pinDebitRate" label="PIN debit rate" suffix="%" min={0} />
                <NumberField control={form.control} name="ebtRate" label="EBT rate" suffix="%" min={0} />
                <NumberField control={form.control} name="offlineDebitRate" label="Offline debit rate" suffix="%" min={0} />
                <PassThroughField control={form.control} name="offlineDebitPassThrough" label="Offline debit pass-through" />
                <PassThroughField control={form.control} name="pinDebitRatePassThrough" label="PIN debit rate pass-through" />
              </Grid>
            </Panel>

            <Panel title="Transaction & Authorization Fees">
              <Grid cols={3}>
                <NumberField control={form.control} name="transactionFee" label="Transaction fee" prefix="$" min={0} />
                <NumberField control={form.control} name="authorizationFee" label="Authorization fee" prefix="$" min={0} />
                <NumberField control={form.control} name="avsFee" label="AVS fee" prefix="$" min={0} />
                <NumberField control={form.control} name="voiceAuthorizationFee" label="Voice authorization fee" prefix="$" min={0} />
                <NumberField control={form.control} name="pinDebitAuthorizationFee" label="PIN debit authorization fee" prefix="$" min={0} />
                <NumberField control={form.control} name="ebtAuthorizationFee" label="EBT authorization fee" prefix="$" min={0} />
                <NumberField control={form.control} name="batchFee" label="Batch fee" prefix="$" min={0} />
                <NumberField control={form.control} name="perTransFee" label="Per-transaction fee" prefix="$" min={0} />
              </Grid>
            </Panel>

            <Panel title="Monthly & Recurring Fees">
              <Grid cols={3}>
                <NumberField control={form.control} name="monthlyFee" label="Monthly fee" prefix="$" min={0} />
                <NumberField control={form.control} name="monthlyDashboardAccessFee" label="Monthly dashboard access fee" prefix="$" min={0} />
                <NumberField control={form.control} name="monthlyPciFee" label="Monthly PCI fee" prefix="$" min={0} />
                <NumberField control={form.control} name="monthlyMinimumFee" label="Monthly minimum fee" prefix="$" min={0} />
                <NumberField control={form.control} name="annualFee" label="Annual fee" prefix="$" min={0} />
                <NumberField control={form.control} name="serviceProviderMonthlyFee" label="Service provider monthly fee" prefix="$" min={0} />
                <NumberField control={form.control} name="otherServiceProviderFee" label="Other service provider fee" prefix="$" min={0} />
              </Grid>
            </Panel>

            <Panel title="One-Time & Equipment Fees">
              <Grid cols={3}>
                <NumberField control={form.control} name="setupFee" label="Setup fee" prefix="$" min={0} />
                <NumberField control={form.control} name="applicationFee" label="Application fee" prefix="$" min={0} />
                <NumberField control={form.control} name="chargebackFee" label="Chargeback fee" prefix="$" min={0} />
                <NumberField control={form.control} name="retrievalFee" label="Retrieval fee" prefix="$" min={0} />
                <NumberField control={form.control} name="gatewayFee" label="Gateway fee" prefix="$" min={0} />
                <NumberField control={form.control} name="wirelessFee" label="Wireless fee" prefix="$" min={0} />
                <NumberField control={form.control} name="posSystemFee" label="POS system fee" prefix="$" min={0} />
              </Grid>
            </Panel>

            {error && <Notice tone="error">{error}</Notice>}

            <div className="flex items-center justify-end gap-3">
              <Button asChild variant="ghost">
                <Link to={backTo}>Cancel</Link>
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Saving…" : isEdit ? "Save campaign" : "Create campaign"}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}

function Grid({ children, cols = 2 }: { children: ReactNode; cols?: 2 | 3 }) {
  const cls =
    cols === 3
      ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
      : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4";
  return <div className={cls}>{children}</div>;
}

function PassThroughField({
  control, name, label,
}: {
  control: Control<FormValues>;
  name: FieldPath<FormValues>;
  label: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-center gap-2 space-y-0 self-end mb-2">
          <FormControl>
            <Checkbox checked={!!field.value} onCheckedChange={(c) => field.onChange(c === true ? 1 : 0)} />
          </FormControl>
          <FormLabel className="text-sm font-normal text-slate-700">{label}</FormLabel>
        </FormItem>
      )}
    />
  );
}

function blankCampaign(): CampaignDetail {
  return {
    id: "",
    name: "",
    description: null,
    isPublic: true,
    campaignType: "Acquiring",
    acquirer: null,
    boardingApplicationType: "Acquiring",
    giactKbaEnabled: false,
    status: "Active",
    acquiringFeeSpecialNote: null,
    achFeeSpecialNote: null,
    tsysAssociation: null,
    leadSource: null,
    passThroughInterchangePlus: null,
    pinDebitRate: null,
    ebtRate: null,
    transactionFee: null,
    authorizationFee: null,
    avsFee: null,
    voiceAuthorizationFee: null,
    pinDebitAuthorizationFee: null,
    ebtAuthorizationFee: null,
    monthlyDashboardAccessFee: null,
    monthlyPciFee: null,
    monthlyMinimumFee: null,
    monthlyFee: null,
    applicationFee: null,
    annualFee: null,
    batchFee: null,
    chargebackFee: null,
    retrievalFee: null,
    qualifiedRate: null,
    midQualSurcharge: null,
    nonQualSurcharge: null,
    visaMcDiscoverRate: null,
    americanExpressOptBlueRate: null,
    offlineDebitRate: null,
    offlineDebitPassThrough: null,
    pinDebitRatePassThrough: null,
    gatewayFee: null,
    wirelessFee: null,
    posSystemFee: null,
    perTransFee: null,
    setupFee: null,
    serviceProviderMonthlyFee: null,
    otherServiceProviderFee: null,
    createdAt: "",
    updatedAt: "",
  };
}
